import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IVerticalBarChartData } from 'reports/components/charts';
import {
  QuotesDashboardDateRange,
  QuotesDashboardQuoteType,
  QuotesVsOrdersView,
} from 'reports/components/QuotesDashboard';
import { IQuotesDashboardDetails, IQuotesDashboardDetailsItem } from 'reports/types';

import { aggregateToMonthly, formatLabel, isMonthlyAggregation } from './utils';

const CHART_COLORS = {
  orders: '#125DCB',
  quotes: '#6FAFFF',
};

interface IProps {
  data?: IQuotesDashboardDetails;
  dateRange: QuotesDashboardDateRange;
  type: QuotesDashboardQuoteType;
  view: QuotesVsOrdersView;
}

export const useChartData = ({ data, dateRange, type, view }: IProps): IVerticalBarChartData => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!data) {
      return { datasets: [], labels: [] };
    }

    const isCustomer = type === QuotesDashboardQuoteType.CUSTOMER;
    const isMonthly = isMonthlyAggregation(dateRange);

    const quotesData = isCustomer ? data.customerQuotes : data.vendorQuotes;
    const ordersData = isCustomer ? data.customerOrders : data.vendorOrders;

    const processedQuotes = isMonthly ? aggregateToMonthly(quotesData || []) : quotesData || [];
    const processedOrders = isMonthly ? aggregateToMonthly(ordersData || []) : ordersData || [];

    const allDates = new Set<string>();
    processedQuotes.forEach((item) => allDates.add(item.date));
    processedOrders.forEach((item) => allDates.add(item.date));

    const sortedDates = Array.from(allDates).sort();

    const quotesMap = new Map<string, IQuotesDashboardDetailsItem>();
    const ordersMap = new Map<string, IQuotesDashboardDetailsItem>();

    processedQuotes.forEach((item) => quotesMap.set(item.date, item));
    processedOrders.forEach((item) => ordersMap.set(item.date, item));

    const getValue = (item: IQuotesDashboardDetailsItem | undefined): number => {
      if (!item) {
        return 0;
      }

      if (view === QuotesVsOrdersView.QUANTITY) {
        return item.count;
      }

      return isCustomer ? item.totalPrice : item.totalCost;
    };

    const labels = sortedDates.map((date) => formatLabel(date, isMonthly));
    const quotesValues = sortedDates.map((date) => getValue(quotesMap.get(date)));
    const ordersValues = sortedDates.map((date) => getValue(ordersMap.get(date)));

    return {
      datasets: [
        {
          backgroundColor: CHART_COLORS.quotes,
          data: quotesValues,
          label: t('labelQuotes'),
        },
        {
          backgroundColor: CHART_COLORS.orders,
          data: ordersValues,
          label: t('labelOrders'),
        },
      ],
      labels,
    };
  }, [data, dateRange, t, type, view]);
};
